/**
 * Asking for a report.
 *
 * **This form starts a run. It does not produce a file, and that distinction is
 * the whole slice.** The rejected shape was a button that posts some filters and
 * streams back a spreadsheet: it works, it is fewer moving parts, and it cannot
 * be scheduled later without being rewritten — when a schedule fires there is no
 * request, no signed-in user and nowhere to put the file. So what this panel
 * submits is a *definition*, what comes back is a *run*, and the nightly
 * schedule submits the same definition to the same generator. The button is one
 * of two ways a run starts; it is not the interesting one.
 *
 * **The two named periods are resolved with the scheduler's own function.**
 * "The month that just ended" is `resolveReportWindow('previous_month', …)` —
 * the same call the scheduler makes at tick time — so a run started here and a
 * run started unattended cover the same instants rather than two windows that
 * happen to agree today. That is also why the resolved boundaries are printed
 * below the choice before anything is submitted: the window that is about to be
 * stored on the run is the one on the screen, spelled "up to but not including"
 * rather than as a bracket only four people per organisation read correctly.
 *
 * **`now` ticks while this panel is open.** A screen left open across midnight
 * would otherwise submit yesterday's yesterday under a label reading "the day
 * that just ended". The previewed window is the window submitted — what you saw
 * is what you asked for — and the same clock decides whether the "this period
 * has not finished yet" warning is still true.
 *
 * **A custom period is picked as inclusive days and stored half-open.** People
 * think "the 1st to the 30th, including the 30th"; windows tile without gaps
 * only if the upper bound is exclusive. `customReportWindow` converts, and the
 * sentence underneath states the real boundaries, so the conversion is visible
 * rather than trusted. `reportFileName` makes the same deliberate mismatch in
 * the other direction and documents why.
 *
 * **Slice 44 gave `REPORT_KIND_VALUES` its second member, and this panel its
 * kind picker** — the day the paragraph that stood here said would come.
 * Credit usage, or Results by surface — of the kinds this deployment's
 * database can record (`GET /reports/kinds`): a kind whose migration has not
 * reached the database is not offered, because its run would be refused at
 * the insert with nothing a person could do about it. Until that answer
 * arrives, or if it does not, the picker offers credit usage alone — the one
 * kind every database has held since slice 13. What the chosen workbook contains is
 * still stated as its sheets' names, in the contract's own words, because the
 * reader's question is "what will I get", not "which enum member" — and it is
 * the picker's own description, under it and announced with it, because a
 * line above the control that changes it changes out of sight. There is still
 * no format picker: there is one format.
 *
 * **The period lines are the kind's.** A closed day reproduces a credit usage
 * workbook, and a closed month is what its monthly schedule resolves to.
 * Neither is true of Results by surface: delivery events keep arriving and
 * Meta revises past days, so a re-run of the same days can differ, and it has
 * no schedule. So its choices say only what they are, and the credit
 * ledger's "movement at the closing instant" is said about campaigns there.
 *
 * **What this panel deliberately does not claim.** It says nothing about a
 * report arriving nightly. A schedule may or may not be running for this
 * workspace; this app cannot see one, and a sentence promising a nightly file
 * would be a claim about somebody else's cron. When a scheduled run exists it
 * shows up in the list below with its own trigger printed on it — a fact from
 * the record rather than a promise from a screen.
 *
 * **Nothing here can send anything.** A report is read-only — credit usage
 * over `credit_ledger`, results by surface over campaigns, delivery events and
 * the ad account; there is no address, no channel and no recipient anywhere in
 * this form, and the file it eventually produces is fetched through the tenant's
 * own authenticated path rather than mailed anywhere.
 *
 * The two paragraphs that carry the load — the statement of the period that is
 * about to be stored, and what an empty period means — are in `ReportCopy.tsx`,
 * because this file imports the API client and therefore cannot be rendered by
 * `scripts/verify-report-screens.tsx`. See that file's header.
 *
 * **Slice 17 shortened three sentences here and dropped none of them.** The
 * description still names both worksheets and still says they are kept apart,
 * because a reader who does not know that will add two columns of different
 * balances together; what went was the explanation of *why* a mixed sheet
 * invites that, which is an argument and is two paragraphs above. The
 * unfinished-period callout still says the numbers will change on a re-run — the
 * only reason it exists — without restating that a closed period is the
 * reproducible one, which the period choices already say.
 */
import {
  CREDIT_USAGE_SHEET_NAMES,
  MAX_REPORT_WINDOW_DAYS,
  REPORT_KIND_VALUES,
  REPORT_TITLES,
  REPORT_WINDOW_RULE_LABELS,
  createReportRunRequestSchema,
  describeReportWindow,
  reportKindSchema,
  surfaceResultsSheetNames,
  type ReportKind,
  type ReportWindow,
} from '@wend/shared';
import {
  Button,
  Callout,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  FormField,
  Input,
  Select,
  useToast,
  type SelectOption,
} from '@wend/ui';
import { FileSpreadsheet } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';

import { FormAlert } from '../../components/FormAlert';
import { ROOT_FIELD, apiFieldErrors, errorMessage, isApiError } from '../../lib/api/errors';
import { createReportRun, fetchReportKinds } from '../../lib/api/reports';
import { browserTimeZone } from '../../lib/campaign-display';
import { validate, type FormErrors } from '../../lib/form';
import { useAsyncData } from '../../lib/use-async-data';
import {
  REPORT_PERIOD_CHOICE_VALUES,
  isReportWindowRule,
  reportWindowForChoice,
  toDateInputValue,
  type ReportPeriodChoice,
} from '../../lib/report-display';
import { ReportEmptyPeriodNote, ReportPeriodPreview } from './ReportCopy';

/**
 * How often the clock behind the two named periods is refreshed. A minute is
 * short enough that "the day that just ended" is never meaningfully stale and
 * long enough that an idle tab is doing nothing worth measuring.
 */
const NOW_TICK_MS = 60_000;

/** The label each choice carries. The two rules take the contract's own words. */
const PERIOD_CHOICE_LABEL: Readonly<Record<ReportPeriodChoice, string>> = {
  previous_day: REPORT_WINDOW_RULE_LABELS.previous_day,
  previous_month: REPORT_WINDOW_RULE_LABELS.previous_month,
  custom: 'Specific dates',
};

/** The kind every database has recorded since slice 13 — offered before, or without, the kinds read. */
const ALWAYS_RECORDABLE: readonly ReportKind[] = ['credit_usage'];

/** The kinds offered, by the name a reader recognises, in the contract's order. */
function kindOptions(recordable: readonly ReportKind[]): readonly SelectOption[] {
  return REPORT_KIND_VALUES.filter((value) => recordable.includes(value)).map((value) => ({
    value,
    label: REPORT_TITLES[value],
  }));
}

/** The worksheets of Results by surface, as the workbook writes them — the built surfaces and the ad account. */
const SURFACE_SHEETS: readonly string[] = surfaceResultsSheetNames();

/** Each choice's line, per kind — see the header for why Results by surface's say less. */
const PERIOD_CHOICE_DESCRIPTION: Readonly<Record<ReportKind, Readonly<Record<ReportPeriodChoice, string>>>> = {
  credit_usage: {
    previous_day: 'A closed day, so the same run can be repeated later and give the same workbook.',
    previous_month: 'A closed month — the period a monthly schedule resolves to.',
    custom: 'Both dates are included in the report.',
  },
  surface_results: {
    previous_day: 'A closed day.',
    previous_month: 'A closed month.',
    custom: 'Both dates are included in the report.',
  },
};

/** Yesterday, as a date input reads it — a valid one-day window to start from. */
function yesterdayValue(): string {
  const date = new Date();
  date.setDate(date.getDate() - 1);
  return toDateInputValue(date);
}

/**
 * Every complaint that no field on this form is showing.
 *
 * The two date inputs render `window.from` and `window.to` themselves, and only
 * when they are on screen. Anything else the contract refuses — a zone it will
 * not accept, a window error while a *named* period is selected and there is no
 * field to hang it on — would otherwise be dropped silently, and a button that
 * refuses without saying why is indistinguishable from a button that is broken.
 */
function unattachedErrors(errors: FormErrors, shownOnFields: readonly string[]): string[] {
  const shown = new Set([ROOT_FIELD, ...shownOnFields]);
  return Object.entries(errors)
    .filter(([key]) => !shown.has(key))
    .map(([, message]) => message);
}

export interface RequestReportPanelProps {
  /** Fired after a run is created, so the list below re-reads rather than guessing. */
  onRunStarted: () => void;
}

export function RequestReportPanel({ onRunStarted }: RequestReportPanelProps) {
  const { toast } = useToast();

  // The zone the boundaries below are chosen in, and the zone the sheet prints.
  // Reused rather than re-derived: `resolved: false` means Intl would not name
  // the zone, which is a sentence this panel owes the reader.
  const zone = useMemo(browserTimeZone, []);

  const [kind, setKind] = useState<ReportKind>('credit_usage');
  const recordable = useAsyncData((signal) => fetchReportKinds(signal), []);
  const offered = recordable.data ?? ALWAYS_RECORDABLE;
  const [choice, setChoice] = useState<ReportPeriodChoice>('previous_month');
  const [firstDay, setFirstDay] = useState(yesterdayValue);
  const [lastDay, setLastDay] = useState(yesterdayValue);
  const [now, setNow] = useState(() => new Date());
  const [errors, setErrors] = useState<FormErrors>({});
  const [requestId, setRequestId] = useState<string | undefined>(undefined);
  const [pending, setPending] = useState(false);

  const namedPeriod = isReportWindowRule(choice);

  // Ticks whichever period is selected, not only the two that resolve against a
  // clock: the "this has not finished yet" check below is also a comparison with
  // now, and a frozen clock there leaves that warning standing on a period that
  // has since closed.
  useEffect(() => {
    const timer = window.setInterval(() => {
      setNow(new Date());
    }, NOW_TICK_MS);
    return () => {
      window.clearInterval(timer);
    };
  }, []);

  const period: ReportWindow | null = reportWindowForChoice(choice, now, zone.timezone, {
    firstDay,
    lastDay,
  });

  // A window that has not finished yet is allowed — it is an explicit pair of
  // instants, not a rule that ends "now" — but it is worth saying out loud that
  // re-running it later will not reproduce it.
  const endsInFuture = period !== null && Date.parse(period.to) > now.getTime();

  function clearErrors(): void {
    setErrors({});
    setRequestId(undefined);
  }

  async function onSubmit(): Promise<void> {
    if (pending) return;
    setRequestId(undefined);

    if (period === null) {
      setErrors({
        [ROOT_FIELD]: namedPeriod
          ? `Wend could not work out that period in ${zone.timezone}, so it has asked for nothing rather than reading the wrong days.`
          : 'Pick a first and a last day.',
      });
      return;
    }

    const result = validate(createReportRunRequestSchema, {
      kind,
      format: 'xlsx',
      window: period,
    });

    if (!result.ok) {
      setErrors(result.errors);
      return;
    }

    setErrors({});
    setPending(true);

    try {
      const run = await createReportRun(result.data);
      // What the toast can promise is what the API just recorded: a run exists,
      // over this period. It does not promise a file, or that the list below has
      // already re-read — the list says both of those for itself.
      toast({
        tone: 'success',
        title: 'Report started',
        description: `Covering ${describeReportWindow(run.definition.window)}.`,
      });
      onRunStarted();
    } catch (error) {
      setErrors({ ...apiFieldErrors(error), [ROOT_FIELD]: errorMessage(error) });
      setRequestId(isApiError(error) ? error.requestId : undefined);
    } finally {
      setPending(false);
    }
  }

  const complaints = unattachedErrors(errors, namedPeriod ? [] : ['window.from', 'window.to']);

  return (
    <Card>
      <CardHeader>
        <CardTitle>Request a report</CardTitle>
      </CardHeader>

      <CardContent className="flex max-w-2xl flex-col gap-5">
        {errors[ROOT_FIELD] !== undefined && (
          <FormAlert tone="error" requestId={requestId}>
            {errors[ROOT_FIELD]}
          </FormAlert>
        )}

        {complaints.length > 0 && <FormAlert tone="error">{complaints.join(' ')}</FormAlert>}

        <FormField
          label="Report"
          className="max-w-xl"
          description={
            kind === 'credit_usage' ? (
              <>
                Built from the credit ledger, with one worksheet per channel:{' '}
                <span className="text-text">{CREDIT_USAGE_SHEET_NAMES.email}</span> and{' '}
                <span className="text-text">{CREDIT_USAGE_SHEET_NAMES.whatsapp}</span>. Kept apart — an
                email credit and a WhatsApp credit are separate balances.
              </>
            ) : (
              <span data-role="surface-results-sheets">
                Worksheets: <span className="text-text">{SURFACE_SHEETS.join(', ')}</span>.
              </span>
            )
          }
        >
          <Select
            className="max-w-xs"
            size="sm"
            aria-label="Report"
            options={kindOptions(offered)}
            value={kind}
            disabled={pending}
            onValueChange={(value) => {
              const parsed = reportKindSchema.safeParse(value);
              if (parsed.success) setKind(parsed.data);
              clearErrors();
            }}
          />
        </FormField>

        <fieldset className="flex flex-col gap-2.5" disabled={pending}>
          <legend className="mb-1.5 text-[0.85rem] font-medium text-text">Period</legend>

          {REPORT_PERIOD_CHOICE_VALUES.map((value) => (
            <PeriodChoice
              key={value}
              checked={choice === value}
              label={PERIOD_CHOICE_LABEL[value]}
              description={PERIOD_CHOICE_DESCRIPTION[kind][value]}
              onSelect={() => {
                setChoice(value);
                // A named period is resolved against the clock at the moment it
                // is chosen, not the moment this screen was opened.
                setNow(new Date());
                clearErrors();
              }}
            />
          ))}
        </fieldset>

        {!namedPeriod && (
          <div className="flex flex-wrap gap-4">
            <FormField
              label="First day"
              description="Included."
              error={errors['window.from']}
              className="min-w-[12rem] flex-1"
            >
              <Input
                type="date"
                value={firstDay}
                disabled={pending}
                onChange={(event) => {
                  setFirstDay(event.target.value);
                  clearErrors();
                }}
              />
            </FormField>

            <FormField
              label="Last day"
              description={`Included. At most ${String(MAX_REPORT_WINDOW_DAYS)} days in one report.`}
              error={errors['window.to']}
              className="min-w-[12rem] flex-1"
            >
              <Input
                type="date"
                value={lastDay}
                disabled={pending}
                onChange={(event) => {
                  setLastDay(event.target.value);
                  clearErrors();
                }}
              />
            </FormField>
          </div>
        )}

        <ReportPeriodPreview
          period={period}
          timezone={zone.timezone}
          zoneResolved={zone.resolved}
          namedPeriod={namedPeriod}
          kind={kind}
        />

        {endsInFuture && (
          <Callout tone="warn" title="This period has not finished yet" live="polite">
            It covers only what has happened so far, and running it again later gives different
            numbers for the same dates.
          </Callout>
        )}

        {/* Credit usage's own sentence; Results by surface says so per sheet. */}
        {kind === 'credit_usage' && <ReportEmptyPeriodNote />}

        <div>
          <Button
            type="button"
            loading={pending}
            disabled={period === null}
            leadingIcon={<FileSpreadsheet aria-hidden="true" className="size-4" />}
            onClick={() => {
              void onSubmit();
            }}
          >
            {pending ? 'Starting' : 'Start this report'}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

/** One period option. Mirrors the schedule screen's control, for one habit. */
function PeriodChoice({
  checked,
  onSelect,
  label,
  description,
}: {
  checked: boolean;
  onSelect: () => void;
  label: string;
  description: string;
}) {
  return (
    <label className="flex cursor-pointer items-start gap-3 rounded-base border border-line bg-white/[0.02] px-3.5 py-3 hover:border-line-strong">
      <input
        type="radio"
        name="report-period"
        className="mt-1 size-4 shrink-0 accent-brand-b"
        checked={checked}
        onChange={onSelect}
      />
      <span className="min-w-0">
        <span className="block text-[0.9rem] text-text">{label}</span>
        <span className="mt-0.5 block text-[0.82rem] leading-snug text-muted">{description}</span>
      </span>
    </label>
  );
}
